import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiLogIn, FiArrowLeft, FiShield, FiKey, FiFileText } = FiIcons;

const WhistleblowerReturnLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { reports } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('whistleblower_return_login');

  const [caseId, setCaseId] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Hent retur-parametere hvis de finnes
  const companyId = searchParams.get('c');
  const deptId = searchParams.get('dept');

  const handleBack = () => {
    if (companyId) {
      navigate(`/s/${companyId}${deptId ? `?dept=${deptId}` : ''}`);
    } else {
      navigate('/whistleblower/login');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Security check
    if (!validateSubmission()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // This is a placeholder for the actual login logic.
      // In a real scenario, you would call a function from AuthContext
      // that validates the caseId and accessKey against the backend.
      // await login(caseId, accessKey, 'whistleblower');
      
      console.log('Attempting login with:', { caseId, accessKey });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const report = reports.find(r => r.id === caseId.trim());
      
      if (report) {
        // On success, we navigate to the case detail page.
        navigate(`/varsle/status/${report.id}`);
      } else {
        setError('Ugyldig saksnummer eller tilgangsnøkkel. Vennligst prøv igjen.');
      }
      
    } catch (err) {
      setError('Ugyldig saksnummer eller tilgangsnøkkel. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="inline-block bg-blue-100 text-blue-600 rounded-full p-3">
                <SafeIcon icon={FiShield} className="text-3xl" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mt-4">Saksportal for Varslere</h1>
              <p className="text-gray-500">Logg inn for å se status og kommunisere i din sak.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <HoneypotField />
              <div className="relative">
                <label htmlFor="caseId" className="block text-sm font-medium text-gray-700 mb-1">Saksnummer</label>
                <SafeIcon icon={FiFileText} className="absolute left-3 top-10 text-gray-400" />
                <input
                  id="caseId"
                  type="text"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  placeholder="Skriv inn ditt saksnummer"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>

              <div className="relative">
                <label htmlFor="accessKey" className="block text-sm font-medium text-gray-700 mb-1">Tilgangsnøkkel</label>
                <SafeIcon icon={FiKey} className="absolute left-3 top-10 text-gray-400" />
                <input
                  id="accessKey"
                  type="password"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="Skriv inn din tilgangsnøkkel"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>

              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border-l-4 border-red-400 text-red-700 p-4 rounded-md text-sm"
                >
                  {error}
                </motion.div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-transform transform hover:scale-105 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiLogIn} className="mr-2" />
                  {loading ? 'Logger inn...' : 'Logg inn'}
                </button>
              </div>
            </form>
          </div>
          <div className="bg-gray-50 px-8 py-4 border-t">
            <button 
              onClick={handleBack} 
              className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-blue-600 font-medium group"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2 group-hover:-translate-x-1 transition-transform"/> 
              <span>{companyId ? 'Tilbake til varslingssiden' : 'Opprette en ny varsling?'}</span>
            </button>
          </div>
        </motion.div>
        
        <p className="text-center text-xs text-gray-500 mt-6">
          Din anonymitet er beskyttet. All kommunikasjon er kryptert.
        </p>
      </div>
    </div>
  );
};

export default WhistleblowerReturnLogin;